<template>
  <s-page-block size="md">
    <s-page-header :overline="t('page.overline')" :heading="task.title" class="mb-2" />

    <s-badge-group class="mb-4">
      <s-badge icon="account" color="white">
        {{ t('page.student-count', {count: studentCount}) }}
      </s-badge>
      <s-badge icon="pencil" color="white">
        {{ t('page.response-count', {count: responseCount}) }}
      </s-badge>
    </s-badge-group>

    <s-card v-if="isAiGraded">
      <template #header>
        <div class="flex items-center justify-between gap-3">
          <h2 class="heading">{{ t('chart.title') }}</h2>
          <s-tooltip>
            <template #tooltip>
              <p class="inline-block max-w-md leading-snug">{{ t('errors-tooltip') }}</p>
            </template>
            <s-badge color="white">
              {{
                t('page.error-count', {
                  count: mistakeLabelClusterData
                    ? mistakeLabelClusterData.clusters.reduce(
                        (acc, cluster) => acc + cluster.counts,
                        0
                      )
                    : 0,
                })
              }}
            </s-badge>
          </s-tooltip>
        </div>
      </template>
      <div class="prose">
        <i18n-t keypath="page.ai-graded-task-description" tag="p">
          <template v-slot:errors>
            <s-tooltip>
              <template #tooltip>
                <p class="inline-block max-w-md leading-snug">{{ t('errors-tooltip') }}</p>
              </template>
              <em class="font-medium text-blue-600 cursor-help">
                {{ t('errors') }}
              </em>
            </s-tooltip>
          </template>
        </i18n-t>
        <p class="text-sm italic">{{ t('page.ai-graded-disclaimer') }}</p>
      </div>

      <task-mistake-labels-pie-chart
        v-if="mistakeLabelClusterData"
        :cluster-data="mistakeLabelClusterData"
      />
      <s-placeholder v-else class="mt-4">
        {{ t('placeholder.error-label-cluster-data') }}
      </s-placeholder>
    </s-card>
    <s-placeholder v-else>
      {{ t('error.not-ai-graded') }}
    </s-placeholder>
  </s-page-block>
</template>
<script setup lang="ts">
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import SCard from '../../design-system/SCard.vue';
import STooltip from '../../design-system/STooltip.vue';
import SBadgeGroup from '../../design-system/SBadgeGroup.vue';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import SBadge from '../../design-system/SBadge.vue';
import {useI18n} from 'vue-i18n';
import StudentMistakeLabelClustersResults = App.DTOs.Analytics.StudentMistakeLabelClustersResults;
import TaskMistakeLabelsPieChart from '../Admin/AiAnalytics/Task/TaskMistakeLabelsPieChart.vue';
import {Course} from '../../types/entities/course.js';
import {Assignment} from '../../types/entities/assignment.js';
import {Task} from '../../types/entities/tasks.js';

defineProps<{
  course: Course;
  assignment: Assignment;
  task: Task;
  responseCount: number;
  studentCount: number;
  isAiGraded: boolean;
  mistakeLabelClusterData: StudentMistakeLabelClustersResults;
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});
</script>
<i18n>
{
  "en": {
    "page.overline": "Assignment Task Analytics",
    "placeholder.error-label-cluster-data": "No Student Error Labels Data Exists for this Task",
    "page.student-count": "1 Student | {count} Students",
    "page.response-count": "1 Response | {count} Responses",
    "chart.title": "Overall Count (%) of Error Modes by Category",
    "error.not-ai-graded": "This task is not AI graded.",
    "page.ai-graded-task-description": "The percentages in the chart correspond to the total number of {errors} recorded for this task in the given category. These {errors} are aggregated over all responses and all students.",
    "page.ai-graded-disclaimer": "Minor errors are ignored in these statistics.",
    "page.error-count": "{count} Errors",
    "errors": "errors",
    "errors-tooltip": "Students not receiving full credit have their responses automatically characterized by our algorithms. The reasons for their deductions are categorized and referred to as errors."
  },
  "fr": {
    "page.overline": "Analyse des tâches d'affectation",
    "placeholder.error-label-cluster-data": "Aucune donnée d'étiquette d'erreur d'étudiant n'existe pour cette tâche",
    "page.student-count": "{count} Étudiants",
    "page.response-count": "{count} Réponses",
    "chart.title": "Nombre total (%) d'erreurs par catégorie",
    "page.ai-graded-task-description": "Les pourcentages dans le graphique correspondent au nombre total d'{errors} enregistrées pour cette tâche dans la catégorie donnée. Ces {errors} sont agrégées sur toutes les réponses et tous les étudiants.",
    "error.not-ai-graded": "Cette tâche n'est pas évaluée par l'IA.",
    "page.ai-graded-disclaimer": "Les erreurs mineures sont ignorées dans ces statistiques.",
    "page.error-count": "{count} Erreurs",
    "errors": "erreurs",
    "errors-tooltip": "Les étudiants qui n'ont pas reçu la note maximale ont leurs réponses automatiquement caractérisées par nos algorithmes. Les raisons de leurs déductions sont catégorisées et désignées sous le nom d'erreurs."
  }
}
</i18n>
